/** Inline style flags for a contiguous run of template text. */
export interface TemplateInlineStyle {
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

export interface TemplateTextRun {
  text: string;
  style: TemplateInlineStyle;
}

const ALLOWED_TAGS = new Set(['b', 'strong', 'i', 'em', 'u', 'br']);

const EMPTY_STYLE: TemplateInlineStyle = {
  bold: false,
  italic: false,
  underline: false,
};

/** True when content includes any inline formatting tags. */
export function hasTemplateRichText(content: string): boolean {
  return /<\/?(?:b|strong|i|em|u|br)\b/i.test(content);
}

/** Strip tags; keep line breaks as newlines. */
export function plainTextFromTemplateRichText(content: string): string {
  return parseTemplateRichText(content)
    .map((run) => run.text)
    .join('');
}

/**
 * Sanitize HTML from a contenteditable so only b/i/u/br remain.
 * Existing plain-text templates pass through unchanged.
 */
export function sanitizeTemplateRichText(html: string): string {
  if (!html) {
    return '';
  }
  if (typeof DOMParser === 'undefined') {
    return html.replace(/<[^>]+>/g, '');
  }

  const doc = new DOMParser().parseFromString(
    `<div id="root">${html}</div>`,
    'text/html'
  );
  const root = doc.getElementById('root');
  if (!root) {
    return '';
  }

  normalizeBlockBreaks(root);
  const cleaned = cleanNode(root);
  return serializeCleaned(cleaned).replace(/\u00a0/g, ' ');
}

/** Parse sanitized (or plain) content into styled runs for PDF / preview. */
export function parseTemplateRichText(content: string): TemplateTextRun[] {
  if (!content) {
    return [];
  }
  if (!hasTemplateRichText(content)) {
    return [{ text: content, style: { ...EMPTY_STYLE } }];
  }

  if (typeof DOMParser === 'undefined') {
    return [{ text: content.replace(/<[^>]+>/g, ''), style: { ...EMPTY_STYLE } }];
  }

  const doc = new DOMParser().parseFromString(
    `<div id="root">${content}</div>`,
    'text/html'
  );
  const root = doc.getElementById('root');
  if (!root) {
    return [];
  }

  const runs: TemplateTextRun[] = [];
  walk(root, { ...EMPTY_STYLE }, runs);
  return mergeAdjacentRuns(runs);
}

function normalizeBlockBreaks(root: HTMLElement): void {
  const blocks = root.querySelectorAll('div, p');
  blocks.forEach((block) => {
    if (block.previousSibling) {
      block.parentNode?.insertBefore(root.ownerDocument.createElement('br'), block);
    }
    while (block.firstChild) {
      block.parentNode?.insertBefore(block.firstChild, block);
    }
    block.remove();
  });
}

function cleanNode(node: Node): Node[] {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ? [node] : [];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') {
    return [el.ownerDocument.createElement('br')];
  }

  const children: Node[] = [];
  Array.from(el.childNodes).forEach((child) => {
    children.push(...cleanNode(child));
  });

  if (!ALLOWED_TAGS.has(tag) || tag === 'br') {
    return children;
  }

  const wrapper = el.ownerDocument.createElement(normalizeTag(tag));
  children.forEach((child) => wrapper.appendChild(child.cloneNode(true)));
  return [wrapper];
}

function normalizeTag(tag: string): 'b' | 'i' | 'u' {
  if (tag === 'strong' || tag === 'b') {
    return 'b';
  }
  if (tag === 'em' || tag === 'i') {
    return 'i';
  }
  return 'u';
}

function serializeCleaned(nodes: Node[]): string {
  return nodes.map((node) => serializeNode(node)).join('');
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeText(node.textContent ?? '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === 'br') {
    return '<br>';
  }
  const inner = Array.from(el.childNodes).map(serializeNode).join('');
  if (!inner) {
    return '';
  }
  return `<${tag}>${inner}</${tag}>`;
}

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function walk(
  node: Node,
  style: TemplateInlineStyle,
  runs: TemplateTextRun[]
): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    if (text) {
      runs.push({ text, style: { ...style } });
    }
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === 'br') {
    runs.push({ text: '\n', style: { ...style } });
    return;
  }

  const next = { ...style };
  if (tag === 'b' || tag === 'strong') {
    next.bold = true;
  }
  if (tag === 'i' || tag === 'em') {
    next.italic = true;
  }
  if (tag === 'u') {
    next.underline = true;
  }

  Array.from(el.childNodes).forEach((child) => walk(child, next, runs));
}

function mergeAdjacentRuns(runs: TemplateTextRun[]): TemplateTextRun[] {
  const merged: TemplateTextRun[] = [];
  for (const run of runs) {
    const prev = merged[merged.length - 1];
    if (
      prev &&
      prev.style.bold === run.style.bold &&
      prev.style.italic === run.style.italic &&
      prev.style.underline === run.style.underline &&
      !prev.text.endsWith('\n') &&
      run.text !== '\n'
    ) {
      prev.text += run.text;
    } else {
      merged.push({ text: run.text, style: { ...run.style } });
    }
  }
  return merged;
}
